import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './IconCarouselSection.css';
import ArtAndDesignIcon from '../../assets/artanddesign.svg';
import LearnIcon from '../../assets/learn.svg';
import PhotographyIcon from '../../assets/photography.svg';
import Hashids from 'hashids';
import api from '../../services/api';

// Icons for categories
import {FaNewspaper, FaBook, FaHeadphones, FaEye} from 'react-icons/fa';

const IconCarouselSection = () => {
    const navigate = useNavigate();
    const hashids = new Hashids('your_secret_salt', 8);

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Function to split long names into two lines
    const splitCategoryName = (name) => {
        // Define which categories should be split and how
        const splitRules = {
            'Mind Body Spirit': ['Mind Body', 'Spirit'],
            'Art, Design & Photography': ['Art, Design &', 'Photography'],
            'Newspapers & Magazines': ['Newspapers &', 'Magazines'],
            'Food & Drink': ['Food &', 'Drink']
        };
        // Check if we have a specific rule for this category
        if (splitRules[name]) {
            return splitRules[name];
        }
        // Generic splitting logic for long names
        if (name.length > 12) { // Adjust threshold as needed
            const words = name.split(' ');
            if (words.length >= 3) {
                // Try to split roughly in the middle
                const mid = Math.ceil(words.length / 2);
                const firstLine = words.slice(0, mid).join(' ');
                const secondLine = words.slice(mid).join(' ');
                return [firstLine, secondLine];
            }
        }
        // Return as single line
        return [name];
    };

    // Map category slugs to icons (using existing mapping logic)
    const getCategoryIcon = (slug, name) => {
        const iconMap = {
            'art-design-and-photography': <img src={ArtAndDesignIcon} alt={name} style={{ width: 30, height: 30 }} />,
            'photography': <img src={PhotographyIcon} alt={name} style={{ width: 30, height: 30 }} />,
            'learn': <img src={LearnIcon} alt={name} style={{ width: 30, height: 30 }} />,
            'sport': <img src="/assets/sport.png" alt={name} style={{ width: 30, height: 30 }} />,
            'performance': <img src="/assets/performance.png" alt={name} style={{ width: 30, height: 30 }} />,
            'mind-body-spirit': <img src="/assets/mindbodyspirit.png" alt={name} style={{ width: 30, height: 30 }} />,
            'newspapers-and-magazines': <FaNewspaper size={30} />,
            'written-word': <FaBook size={30} />,
            'music-and-podcasts': <FaHeadphones size={30} />,
            'video': <FaEye size={30} />,
        };

        return iconMap[slug] || <div className="default-icon">{name.charAt(0)}</div>;
    };

    // Fetch categories from API
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoading(true);
                const response = await api.get('/categories/categories/?page=1&page_size=20');
                const data = await response.data;
                // Transform API data to match component structure
                const transformedCategories = data.categories.map(category => {
                    const lines = splitCategoryName(category.name);
                    return {
                        icon: getCategoryIcon(category.slug, category.name),
                        label: category.name,
                        displayLines: lines, // Array of lines to display
                        urlParam: category.slug,
                        id: category.id,
                    };
                });

                setCategories(transformedCategories);
                setError(null);
            } catch (err) {
                console.error('Error fetching categories:', err);
                setError('Failed to load categories');
                // Optionally set default categories or leave empty
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    // Handler for category click
    const handleCategoryClick = (category) => {
        const maskedId = hashids.encode(category.id);
        navigate(`/${category.urlParam}/${maskedId}`);
    };

    // Loading state
    if (loading) {
        return (
            <div className="icon-carousel-section">
                <div className="icon-carousel-container">
                    <div className="loading-message">Loading categories...</div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="icon-carousel-section">
                <div className="icon-carousel-container">
                    <div className="error-message">{error}</div>
                </div>
            </div>
        );
    }

    if (categories.length === 0) {
        return (
            <div className="icon-carousel-section">
                <div className="icon-carousel-container">
                    <div className="empty-message">No categories available</div>
                </div>
            </div>
        );
    }

    return (
        <div className="icon-carousel-section">
            <div className="icon-carousel-container">
                <div className="icon-carousel primary-carousel">
                    {categories.map((category, index) => (
                        <div
                            className="icon-item"
                            key={`category-${category.id || index}`}
                            onClick={() => handleCategoryClick(category)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="icon-circle">
                                {category.icon}
                            </div>
                            <div className="icon-label">
                                {category.displayLines.length === 1 ? (
                                    <h2>{category.displayLines[0]}</h2>
                                ) : (
                                    <h2 className="multi-line-label">
                                        {category.displayLines.map((line, i) => (
                                            <React.Fragment key={i}>
                                                {line}
                                                {i < category.displayLines.length - 1 && <br />}
                                            </React.Fragment>
                                        ))}
                                    </h2>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default IconCarouselSection;